// Floor — live view of every agent, desk and meeting.
// Polls GET /api/office/live every 4 s while mounted; stops on tab hidden; snapshots on return.
// Zero cost anywhere else: no sibling component polls or renders the canvas.
import React, { useEffect, useRef, useState } from "react";
import { api } from "../lib/api";
import {
  PageHeader, Panel, Chip, StatusLight, AgentPortrait,
} from "../ds";
import { createFloor } from "../ds/floor/floorEngine.js";
import { createScene3D } from "../ds/floor/scene3d.js";
import VoicePanel from "../ds/voice/VoicePanel.jsx";

const POLL_MS = 4000;
const MAX_FEED = 50;

function fmtDubaiHM(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return new Intl.DateTimeFormat("en-GB", {
      timeZone: "Asia/Dubai", hour: "2-digit", minute: "2-digit", hour12: false,
    }).format(d);
  } catch { return ""; }
}

function feedLine(ev, nameOf) {
  if (ev.type === "meeting_start") {
    const names = (ev.agents || []).map(nameOf).filter(Boolean).join(", ");
    const tid = ev.task?.id || "";
    const title = ev.task?.title || "";
    return { title: "Meeting", detail: ` ${tid}${title ? " " + title : ""}${names ? ": " + names : ""}` };
  }
  if (ev.type === "meeting_end") {
    return { title: "Meeting", detail: ` ${ev.task?.id || ""} ended` };
  }
  return { title: nameOf(ev.from) || ev.from, detail: ` → ${nameOf(ev.to) || ev.to}: ${ev.text || ""}` };
}

export default function Floor() {
  const stageRef = useRef(null);
  const canvasRef = useRef(null);
  const overlayRef = useRef(null);
  const floorRef = useRef(null);
  const sinceRef = useRef(0);
  const resumedRef = useRef(false);
  const abortRef = useRef(null);
  const intervalRef = useRef(null);
  const nameMapRef = useRef({});

  const [agents, setAgents] = useState([]);
  const [feed, setFeed] = useState([]);

  const kpis = {
    working: agents.filter((a) => a.status === "working").length,
    meeting: agents.filter((a) => a.status === "meeting").length,
    idle: agents.filter((a) => a.status === "idle").length,
    blocked: agents.filter((a) => a.status === "blocked").length,
  };
  const online = agents.filter((a) => a.status !== "offline").length;

  useEffect(() => {
    const canvas = canvasRef.current;
    const overlay = overlayRef.current;
    if (!canvas || !overlay) return;
    const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    let floor;
    try {
      floor = createScene3D({ canvas, overlay, container: stageRef.current, reducedMotion });
    } catch (err) {
      /* WebGL unavailable → fall back to the pixel-art canvas engine. */
      // eslint-disable-next-line no-console
      console.warn("Floor: WebGL not available, falling back to pixel-art engine", err);
      floor = createFloor({ canvas, overlay, reducedMotion });
    }
    floorRef.current = floor;
    floor.start();

    const doResize = () => {
      if (!stageRef.current) return;
      floor.resize(stageRef.current.clientWidth - 8);
    };
    doResize();
    window.addEventListener("resize", doResize);

    const tick = async (firstOrResumed) => {
      try {
        abortRef.current?.abort();
        const ctl = new AbortController();
        abortRef.current = ctl;
        const { data } = await api.get("/office/live", {
          params: { since: firstOrResumed ? 0 : sinceRef.current },
          signal: ctl.signal,
        });
        // Build name map for feed line rendering
        const nm = {};
        for (const a of data.agents || []) nm[a.id] = a.name;
        nameMapRef.current = nm;
        setAgents(data.agents || []);
        if (firstOrResumed) {
          floor.snapshot(data.agents || []);
        } else {
          floor.setAgents(data.agents || []);
          floor.enqueue(data.events || []);
        }
        if ((data.events || []).length) {
          setFeed((prev) => {
            const rows = (data.events || []).map((e) => ({
              id: `ev-${e.seq}`,
              at: e.at,
              ...feedLine(e, (id) => nm[id] || id),
            }));
            // newest on top; cap at MAX_FEED
            const next = [...rows.reverse(), ...prev];
            return next.slice(0, MAX_FEED);
          });
        } else if (firstOrResumed) {
          // Snapshot path: seed feed from the (already) returned events (last 20 in chronological order).
          const seed = (data.events || []).map((e) => ({
            id: `ev-${e.seq}`,
            at: e.at,
            ...feedLine(e, (id) => nm[id] || id),
          }));
          setFeed(seed.reverse().slice(0, MAX_FEED));
        }
        sinceRef.current = data.cursor || sinceRef.current;
      } catch (err) {
        if (err?.name === "CanceledError" || err?.code === "ERR_CANCELED") return;
        // Swallow transient network errors; next tick will retry.
      }
    };

    const startPolling = () => {
      if (intervalRef.current) return;
      intervalRef.current = setInterval(() => tick(false), POLL_MS);
    };
    const stopPolling = () => {
      if (intervalRef.current) { clearInterval(intervalRef.current); intervalRef.current = null; }
      abortRef.current?.abort();
    };

    const onVis = () => {
      if (document.visibilityState === "hidden") {
        floor.stop();
        stopPolling();
        resumedRef.current = true;
      } else {
        tick(true);
        floor.start();
        startPolling();
      }
    };
    document.addEventListener("visibilitychange", onVis);

    tick(true);
    startPolling();

    return () => {
      document.removeEventListener("visibilitychange", onVis);
      window.removeEventListener("resize", doResize);
      stopPolling();
      floor.destroy();
      floorRef.current = null;
    };
  }, []);

  return (
    <div className="p-10 max-w-[1700px] mx-auto space-y-5" data-testid="floor-page">
      <PageHeader
        title="Floor"
        subtitle="Live view of every agent, desk and meeting"
      />

      {/* KPI chip row */}
      <div className="flex items-center gap-2" data-testid="floor-kpis">
        <Chip color="var(--ok)" label={`WORKING · ${kpis.working}`} testId="kpi-working" />
        <Chip color="var(--violet)" label={`IN MEETING · ${kpis.meeting}`} testId="kpi-meeting" />
        <Chip color="var(--muted)" label={`IDLE · ${kpis.idle}`} testId="kpi-idle" />
        <Chip color="var(--bad)" label={`BLOCKED · ${kpis.blocked}`} testId="kpi-blocked" />
      </div>

      <div className="grid gap-5" style={{ gridTemplateColumns: "minmax(0,1fr) 320px" }}>
        {/* Stage */}
        <div className="fl-stage" ref={stageRef} data-testid="floor-stage">
          <div className="fl-scene">
            <canvas className="fl-canvas" ref={canvasRef} data-testid="floor-canvas" />
            <div className="fl-overlay" ref={overlayRef} />
          </div>
        </div>

        {/* Right rail — sticky; order: Voice, Agents, Activity */}
        <div className="fl-right-rail" data-testid="floor-right-rail">
          <VoiceCard />
          <AgentsCard agents={agents} online={online} />
          <ActivityCard feed={feed} />
        </div>
      </div>
    </div>
  );
}

/* ---------- Right-rail cards (sibling-only, no shared state).
   Kept inline so the forthcoming 3D-floor rewrite can lift them unchanged. */

function VoiceCard() {
  return (
    <div data-testid="floor-voice-card">
      <VoicePanel />
    </div>
  );
}

function AgentsCard({ agents, online }) {
  return (
    <div data-testid="floor-agents-card">
      <Panel
        label={`AGENTS · ${online} ONLINE`}
        action={
          <span className="font-mono text-[10.5px] text-[color:var(--muted)]" data-testid="floor-online-count">
            {online} / {agents.length} online
          </span>
        }
        testId="floor-roster-panel"
      >
        {agents.length === 0 ? (
          <div className="text-xs text-[color:var(--muted)]">Loading…</div>
        ) : (
          <ul className="grid grid-cols-2 gap-x-2" data-testid="floor-roster">
            {agents.map((a) => (
              <li key={a.id} className="flex items-center gap-2 py-1.5 min-w-0" data-testid={`roster-${a.id}`}>
                <AgentPortrait agent={{ name: a.name, department: a.department }} size={24} />
                <span className="text-[12.5px] text-[color:var(--fg)] truncate flex-1">{a.name}</span>
                <StatusLight status={a.status === "offline" ? "offline" : a.status === "blocked" ? "blocked" : a.status === "meeting" ? "meeting" : a.status === "working" ? "working" : "idle"} />
              </li>
            ))}
          </ul>
        )}
      </Panel>
    </div>
  );
}

function ActivityCard({ feed }) {
  return (
    <div data-testid="floor-activity-card" className="min-h-0">
      <Panel
        label="ACTIVITY"
        action={<span className="font-mono text-[10.5px] text-[color:var(--muted)]">{feed.length}</span>}
        testId="floor-activity-panel"
      >
        {feed.length === 0 ? (
          <div className="text-xs text-[color:var(--muted)]">No activity yet.</div>
        ) : (
          <ul
            className="fl-activity-scroll divide-y divide-[color:var(--line)]"
            data-testid="floor-activity-scroll"
          >
            {feed.map((row) => (
              <li
                key={row.id}
                className="py-2 grid gap-2"
                style={{ gridTemplateColumns: "48px minmax(0,1fr)" }}
                data-testid="floor-feed-row"
              >
                <time className="font-mono text-[11px] text-[color:var(--dim)] tabular-nums">
                  {fmtDubaiHM(row.at)}
                </time>
                <div className="text-[12.5px] text-[color:var(--fg)] min-w-0">
                  <b className="font-semibold">{row.title}</b>
                  <span className="text-[color:var(--muted)]">{row.detail}</span>
                </div>
              </li>
            ))}
          </ul>
        )}
      </Panel>
    </div>
  );
}
